import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";

import styles from "./courses.module.css";
import { Button, Courses } from "../../../../components";
import { useStore } from "../../../../store";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface Props {
  inSearch?: boolean;
}

const PopularCourses: React.FC<Props> = ({ inSearch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { courseStore } = useStore();

  const [dropdown, setDropdown] = useState(false);
  const [sortOption, setSortOption] = useState("");
  const sortOptions = [
    t("popularCourses.sortOptionMostRelevant"),
    t("popularCourses.sortOptionHighestRated"),
    t("popularCourses.sortOptionPriceLowToHigh"),
    t("popularCourses.sortOptionPriceHighToLow"),
  ];

  const [coursesLimit, setCoursesLimit] = useState(8);
  const [isMoreCoursesClicked, setIsMoreCoursesClicked] = useState(false);

  const [search, setSearch] = useState<string>("");
  const [location, setLocation] = useState<any>(null);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [category, setCategory] = useState<string>("");
  const [placeId, setPlaceId] = useState<string | null>("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  const searchStorage = JSON.parse(sessionStorage.getItem("search") || "{}");

  useEffect(() => {
    if (searchStorage) {
      if (searchStorage.keyword) {
        setSearch(searchStorage.keyword);
      }
      if (searchStorage.domains) {
        setSelectedDomains(searchStorage.domains);
      }
      if (searchStorage.location) {
        setLocation(searchStorage.location.locationName);
        setPlaceId(searchStorage.location.placeId);
      }
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setDropdown]);

  const getSortedCourses = () => {
    const sortedCourses: any[] = [...courses];
    switch (sortOption) {
      case t("popularCourses.sortOptionMostRelevant"):
        return sortedCourses.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      case t("popularCourses.sortOptionHighestRated"):
        return sortedCourses.sort((a, b) => b.rating - a.rating);
      case t("popularCourses.sortOptionPriceLowToHigh"):
        return sortedCourses.sort((a, b) => a.price - b.price);
      case t("popularCourses.sortOptionPriceHighToLow"):
        return sortedCourses.sort((a, b) => b.price - a.price);
      default:
        return sortedCourses;
    }
  };

  const courses = inSearch
    ? courseStore.searchedCourses
    : courseStore.topRatedCourses;

  const isDisabled = inSearch
    ? false
    : courseStore.topRatedCourses.length >= courseStore.topRatedCoursesCount;

  getSortedCourses();

  const getCourseDomain = (course: any) => {
    if (course) {
      if (course.domains) {
        return course.domains[0]?.name || "";
      } else {
        return "";
      }
    } else {
      return "";
    }
  };

  const loadMoreCourse = async () => {
    setIsMoreCoursesClicked(true);
    try {
      const newCoursesLimit = coursesLimit + 4;
      setCoursesLimit(newCoursesLimit);

      inSearch
        ? await searchCourses(newCoursesLimit)
        : await courseStore.getTopRatedCourses(newCoursesLimit);

      getSortedCourses();
    } catch (error) {
      console.log("Error fetching top-rated courses", error);
    } finally {
      setIsMoreCoursesClicked(false);
    }
  };

  const searchCourses = async (limit: number) => {
    try {
      const selectedDomainsIds: any[] = [];
      if (courseStore?.domains && courseStore?.domains.length > 0) {
        courseStore?.domains.forEach((domain: any) => {
          if (selectedDomains.includes(domain.name)) {
            selectedDomainsIds.push(domain.id.toString());
          }
        });
      }

      const searchParams: any = {
        search: search,
        location: placeId,
        categoryId: category,
        domain: selectedDomainsIds,
      };

      await courseStore.searchCourses(limit, searchParams);
      navigate("/courses");
    } catch (error) {
      console.log("Error fetching searched courses", error);
    }
  };

  const capitalizeFirstLetter = (text: string | undefined): string => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const parts: string[] = [];

  if (searchStorage?.keyword) {
    parts.push(capitalizeFirstLetter(searchStorage.keyword));
  }

  if (searchStorage?.domains?.length) {
    parts.push(searchStorage.domains.join(", "));
  }

  const titleText = parts.join(" & ");

  return (
    <div className="flex flex-col gap-5 sm:gap-9">
      {!inSearch && <div className={styles.gradient}></div>}
      <div className={styles.courses}>
        {!inSearch && (
          <>
            <div>
              <h2 className="text-body-large font-body-large-bold sm:text-subheading sm:font-subheading text-primary-purple-800">
                {t("popularCourses.popularCourses")}{" "}
                <span className="hidden sm:inline">
                  {" "}
                  {t("popularCourses.Happening")}
                </span>{" "}
                {t("popularCourses.Now")}
              </h2>
            </div>

            <div className="flex justify-between gap-1">
              <div className="flex flex-col gap-3 w-3/4">
                <p className="text-body-small sm:text-body-medium text-primary-purple-750 w-full">
                  {t("popularCourses.findYourNextClass")}
                </p>
              </div>

              <button
                className={`flex items-center gap-3 w-fit h-fit px-4 py-1.5 rounded-lg text-primary-paradise-pink-500 border ${
                  dropdown
                    ? "bg-primary-paradise-pink-100 border-primary-paradise-pink-500"
                    : "bg-primary-gray-50 border-primary-gray-50"
                }`}
                onClick={() => setDropdown(!dropdown)}
              >
                <img src="/images/icons/sort-pink.svg" width={20} height={20} />
                <span className="body-small">{t("popularCourses.sort")}</span>
              </button>
            </div>
          </>
        )}

        {inSearch && (
          <div className="flex justify-between items-end">
            <div className="body-large text-primary-paradise-pink-800">
              {titleText && titleText + " "}
              {t("navbar.courses")}
              {searchStorage?.location?.locationName?.label &&
                " " + t("in") + " " + searchStorage.location.locationName.label}
            </div>
            <button
              className={`flex items-center gap-3 w-24 px-4 py-1.5 rounded-lg text-primary-paradise-pink-500 border ${
                dropdown
                  ? "bg-primary-paradise-pink-100 border-primary-paradise-pink-500"
                  : "bg-primary-paradise-pink-50 border-primary-paradise-pink-50"
              }`}
              onClick={() => setDropdown(!dropdown)}
            >
              <img src="/images/icons/sort-pink.svg" width={20} height={20} />
              <span className="body-small">{t("popularCourses.sort")}</span>
            </button>
          </div>
        )}

        {getSortedCourses().length > 0 ? (
          <div className={styles.courses_list}>
            {dropdown && (
              <div
                className="absolute top-[-22px] right-0 w-52 bg-white rounded-lg z-10 px-2.5 py-2"
                ref={dropdownRef}
              >
                {sortOptions.map((option, index) => (
                  <div
                    key={index}
                    className={`px-2.5 py-2 cursor-pointer ${
                      sortOption === option
                        ? "text-primary-paradise-pink-500"
                        : "text-primary-gray-850"
                    } ${
                      index === sortOptions.length - 1
                        ? ""
                        : "border-b border-primary-gray-100"
                    }`}
                    onClick={() => {
                      setSortOption(option);
                      setDropdown(false);
                    }}
                  >
                    {option}
                  </div>
                ))}
              </div>
            )}

            {getSortedCourses().map((course: any, index) => (
              <Courses.CourseItem
                key={index}
                type="course"
                course={{
                  id: course?.id,
                  title: course?.title,
                  category: getCourseDomain(course),
                  difficulty: course?.difficulty,
                  description: course?.description,
                  image: course?.image?.name,
                  price: course?.price,
                  rating: course?.rating,
                  ratingCount: course?.rated
                    ? course?.ratingsCount
                    : course?.defaultRatingsCount || 5,
                  provider: {
                    name:
                      course?.courseCreator &&
                      course?.courseCreator !== "" &&
                      course?.courseCreator.toLowerCase().replace(/\s/g, "") !==
                        "wecode" &&
                      course?.courseCreator.toLowerCase().replace(/\s/g, "") !==
                        "wecodeland"
                        ? course?.courseCreator
                        : "ClassX",
                    logo: "/images/we-code.jpeg",
                  },
                  location: course?.isOnline ? "Online" : "Synergy Space",
                  domains: course?.domains?.map(
                    (domain: any) => domain.name || ""
                  ),
                  isOnline: course?.isOnline,
                }}
                inSearch={false}
              />
            ))}
          </div>
        ) : (
          <div className="flex justify-center w-full pt-[74px] sm:pt-[120px] pb-[130px] sm:pb-[200px]">
            <div className="flex flex-col items-center gap-5 max-w-[358px]">
              <img src="/images/icons/no-courses.svg" />
              <div>
                <div className="text-body-medium font-body-medium-bold sm:text-body-large sm:font-body-large text-center">
                  {t("popularCourses.oopsNoResults")}
                </div>
                <p className="text-body-small sm:text-body-medium text-center mt-2.5">
                  {t("popularCourses.noResultsDescription")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {!isMoreCoursesClicked && (
        <div className="flex justify-center">
          <Button
            isDisabled={isDisabled}
            type="secondary"
            text={t("popularCourses.loadMore")}
            width="fit-content"
            onClick={loadMoreCourse}
            customStyle={{ borderRadius: "99px" }}
          />
        </div>
      )}
      {isMoreCoursesClicked && (
        <div className="flex justify-center">
          <button className={styles.load_more} disabled={true}>
            <svg
              aria-hidden="true"
              className="w-6 h-6 text-gray-200 animate-spin fill-[#E83C70]"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default observer(PopularCourses);
